import React from 'react';
import { View, Text, Image, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import Svg, { Path, Rect } from 'react-native-svg';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from 'react-i18next';

import { tokens } from '@design';
import { FONTS } from '../../../constants/config';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getFullMediaUrl } from '../../../utils/media';
import { getTextAlign } from '../../../utils/rtl';
import type { Organization } from '../../../types';

export interface OrganizationBannerProps {
  organization: Pick<
    Organization,
    'id' | 'name' | 'logo' | 'banner_background' | 'banner_tagline_ar' | 'banner_tagline_en'
  >;
  canEdit: boolean;
  onEditPress: () => void;
}

function pickTagline(tagline_ar: string, tagline_en: string, isRTL: boolean): string {
  if (isRTL) return tagline_ar || tagline_en;
  return tagline_en || tagline_ar;
}

// Default banner template (Fujairah skyline line-art, exported from Figma).
// Rendered as the background whenever an org has not uploaded its own.
const DEFAULT_SKYLINE = require('../../../../assets/images/banner-default-skyline.png');

const MUTED = tokens.colors.semantic.general['muted foreground'];

const EditIcon = ({ color }: { color: string }) => (
  <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <Path
      d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const BuildingIcon = ({ color, size = 40 }: { color: string; size?: number }) => (
  <Svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <Path
      d="M16 3l-2 3v3H6v20h20V9h-8V6l-2-3z"
      stroke={color}
      strokeWidth={1.5}
      strokeLinejoin="round"
    />
    <Path d="M16 3v3" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    <Rect x={10} y={13} width={3} height={4} stroke={color} strokeWidth={1.3} />
    <Rect x={19} y={13} width={3} height={4} stroke={color} strokeWidth={1.3} />
    <Rect x={14} y={20} width={4} height={9} stroke={color} strokeWidth={1.3} />
    <Path d="M14 6h4" stroke={color} strokeWidth={1.3} />
  </Svg>
);

export function OrganizationBanner({ organization, canEdit, onEditPress }: OrganizationBannerProps) {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const hasBg = Boolean(organization.banner_background);
  const hasLogo = Boolean(organization.logo);
  const tagline = pickTagline(
    organization.banner_tagline_ar || '',
    organization.banner_tagline_en || '',
    isRTL,
  );
  const hasTag = Boolean(tagline);

  const bgUri = getFullMediaUrl(organization.banner_background);
  const logoUri = getFullMediaUrl(organization.logo);
  // Custom upload wins; otherwise fall back to the bundled default skyline
  // template so every org always shows a banner background.
  const bgSource = hasBg && bgUri ? { uri: bgUri } : DEFAULT_SKYLINE;
  // Per Figma, the bottom-row text always right-aligns to mirror the design
  // (Arabic-first), regardless of the active UI direction.
  const textAlignValue = getTextAlign(true);

  return (
    <View style={styles.shell} accessibilityRole="summary">
      <View style={styles.filledBg}>
        <Image
          source={bgSource}
          style={StyleSheet.absoluteFillObject}
          resizeMode="cover"
        />

        <View style={styles.bottomStrip}>
          <LinearGradient
            colors={['rgba(0,0,0,0)', 'rgba(0,0,0,0.8)']}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={StyleSheet.absoluteFillObject}
            pointerEvents="none"
          />
          <View style={styles.textCol}>
            <Text style={[styles.orgName, { textAlign: textAlignValue }]} numberOfLines={2}>
              {organization.name}
            </Text>
            {hasTag ? (
              <Text style={[styles.tagline, { textAlign: textAlignValue }]} numberOfLines={2}>
                {tagline}
              </Text>
            ) : null}
          </View>

          <View style={styles.logoSquare}>
            {hasLogo && logoUri ? (
              <Image
                source={{ uri: logoUri }}
                style={styles.logoImage}
                resizeMode="contain"
              />
            ) : (
              <BuildingIcon color={MUTED} size={40} />
            )}
          </View>
        </View>

        {canEdit ? (
          <TouchableOpacity
            style={styles.editFab}
            onPress={onEditPress}
            accessibilityLabel={t('myOrganization.banner.editTooltip')}
            hitSlop={8}
          >
            <EditIcon color="#fff" />
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
}

const BANNER_RADIUS = 4;

const styles = StyleSheet.create({
  shell: {
    width: '100%',
    aspectRatio: Platform.OS === 'web' ? 3.2 : 2,
    marginBottom: 16,
  },
  filledBg: {
    flex: 1,
    borderRadius: BANNER_RADIUS,
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  editFab: {
    position: 'absolute',
    bottom: 10,
    start: 10 as any,
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bottomStrip: {
    position: 'absolute',
    start: 0 as any,
    end: 0 as any,
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingTop: 40,
    paddingBottom: 8,
    paddingHorizontal: 8,
    // Gradient overlay (transparent → 80% black, top→bottom) rendered as a
    // sibling LinearGradient instead of a solid backgroundColor.
  },
  textCol: {
    flex: 1,
    flexDirection: 'column',
    gap: 8,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  orgName: {
    color: '#fff',
    fontFamily: FONTS.bold,
    fontSize: 20,
    lineHeight: 26,
  },
  tagline: {
    color: '#fff',
    fontFamily: FONTS.regular,
    fontSize: 16,
    lineHeight: 24,
  },
  logoSquare: {
    width: 80,
    height: 80,
    borderRadius: 2,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  logoImage: { width: '60%', height: '60%' },
});
